import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown } from 'react-icons/fa';
import useApi from '../hooks/useApi';
import { useSeason } from '../contexts/SeasonContext';
import PageHeader from '../components/PageHeader';
import ScrollReveal, { StaggerReveal, StaggerItem } from '../components/ScrollReveal';

const statusCfg = {
  'À venir':  { pill: 'bg-blue-50 text-blue-700',   dot: 'bg-blue-400' },
  'En cours': { pill: 'bg-green-50 text-green-700',  dot: 'bg-green-500' },
  'Terminé':  { pill: 'bg-stone-100 text-stone-400', dot: 'bg-stone-300' },
};

const Badge = ({ status }) => {
  const c = statusCfg[status] || statusCfg['Terminé'];
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full ${c.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {status}
    </span>
  );
};

const EventCard = ({ event, isHighlighted, compact = false }) => {
  const d     = new Date(event.event_date);
  const day   = d.toLocaleDateString('fr-FR', { day: 'numeric' });
  const month = d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '');
  const year  = d.toLocaleDateString('fr-FR', { year: 'numeric' });

  return (
    <StaggerItem>
      <div className={`
        flex overflow-hidden rounded-2xl bg-white border transition-all duration-200
        ${event.status === 'Terminé' ? 'opacity-50' : 'hover:shadow-md'}
        ${isHighlighted ? 'border-brand-800/30 shadow-md' : 'border-stone-100 shadow-sm'}
      `}>
        {/* Date block */}
        <div className={`
          shrink-0 flex flex-col items-center justify-center text-center
          ${compact ? 'w-20 py-5 px-3' : 'w-28 md:w-36 py-8 px-4'}
          ${isHighlighted ? 'bg-brand-800' : 'bg-stone-50'}
        `}>
          <span className={`font-bold leading-none ${compact ? 'text-3xl' : 'text-4xl md:text-5xl'} ${isHighlighted ? 'text-white' : 'text-stone-950'}`}>
            {day}
          </span>
          <span className={`uppercase tracking-widest font-semibold mt-1 ${compact ? 'text-[10px]' : 'text-xs'} ${isHighlighted ? 'text-white/70' : 'text-stone-400'}`}>
            {month}
          </span>
          <span className={`${compact ? 'text-[10px]' : 'text-xs'} mt-0.5 ${isHighlighted ? 'text-white/40' : 'text-stone-300'}`}>
            {year}
          </span>
        </div>

        {/* Content */}
        <div className={`flex-1 min-w-0 flex flex-col justify-center gap-2 ${compact ? 'p-4 md:p-5' : 'p-6 md:p-8'}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <h3 className={`font-semibold ${compact ? 'text-base' : 'text-lg md:text-xl'} ${isHighlighted ? 'text-brand-800' : 'text-stone-950'}`}>
              {event.title}
            </h3>
            <Badge status={event.status} />
          </div>
          {event.description && (
            <p className={`text-stone-500 leading-relaxed ${compact ? 'text-xs' : 'text-sm'}`}>
              {event.description}
            </p>
          )}
        </div>

        {/* Accent bar for highlighted */}
        {isHighlighted && (
          <div className="shrink-0 w-1 bg-brand-800 self-stretch" />
        )}
      </div>
    </StaggerItem>
  );
};

const Accordion = ({ title, isOpen, onClick, count, variant = 'default' }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between rounded-2xl px-6 py-5 font-semibold transition-all duration-200 ${
      variant === 'primary'
        ? 'bg-brand-800 text-white hover:bg-brand-900'
        : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
    }`}
  >
    <span className="flex items-center gap-3 text-base">
      {title}
      <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${
        variant === 'primary' ? 'bg-white/15 text-white' : 'bg-stone-200 text-stone-500'
      }`}>{count}</span>
    </span>
    <motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
      <FaChevronDown size={14} />
    </motion.span>
  </button>
);

function getStatus(dateStr) {
  const now = new Date(); const d = new Date(dateStr);
  now.setHours(0, 0, 0, 0); d.setHours(0, 0, 0, 0);
  if (d < now) return 'Terminé';
  if (d.getTime() === now.getTime()) return 'En cours';
  return 'À venir';
}

const Calendrier = ({ setCurrentSection, currentSection }) => {
  const { data: rawEvents } = useApi('agenda');
  const { season } = useSeason();
  const [pastOpen, setPastOpen]         = useState(false);
  const [upcomingOpen, setUpcomingOpen] = useState(true);

  useEffect(() => { window.scrollTo(0, 0); setUpcomingOpen(true); }, [currentSection]);

  const today = new Date().toISOString().slice(0, 10);

  const events = rawEvents
    .filter(e => {
      if (e.event_date >= today) return true;
      if (!season) return true;
      return e.event_date >= season.start_date && e.event_date <= season.end_date;
    })
    .map(e => ({ ...e, status: getStatus(e.event_date) }))
    .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));

  const upcoming = events.filter(e => e.status !== 'Terminé');
  const past     = events.filter(e => e.status === 'Terminé').reverse();
  const nextIdx  = upcoming.findIndex(e => e.status === 'À venir' || e.status === 'En cours');

  const subtitle = season?.description
    ? `${season.description} — Tous les événements du club`
    : 'Tous les événements du club';

  return (
    <>
      <PageHeader
        title="Calendrier"
        subtitle={subtitle}
        setCurrentSection={setCurrentSection}
        currentSection={currentSection}
      />

      <section className="section bg-stone-50">
        <div className="max-w-6xl mx-auto space-y-4">

          <ScrollReveal>
            <Accordion
              title="Événements à venir"
              isOpen={upcomingOpen}
              onClick={() => setUpcomingOpen(!upcomingOpen)}
              count={upcoming.length}
              variant="primary"
            />
            <AnimatePresence initial={false}>
              {upcomingOpen && (
                <motion.div
                  key="up"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="pt-3 space-y-3">
                    {upcoming.length ? (
                      <StaggerReveal>
                        {upcoming.map((e, i) => (
                          <EventCard key={e.id} event={e} isHighlighted={i === nextIdx} />
                        ))}
                      </StaggerReveal>
                    ) : (
                      <p className="text-sm text-stone-400 py-8 text-center">Aucun événement à venir.</p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <Accordion
              title="Événements passés"
              isOpen={pastOpen}
              onClick={() => setPastOpen(!pastOpen)}
              count={past.length}
            />
            <AnimatePresence initial={false}>
              {pastOpen && (
                <motion.div
                  key="past"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="pt-3 space-y-2">
                    {past.length ? (
                      <StaggerReveal>
                        {past.map(e => (
                          <EventCard key={e.id} event={e} compact />
                        ))}
                      </StaggerReveal>
                    ) : (
                      <p className="text-sm text-stone-400 py-8 text-center">Aucun événement passé.</p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </ScrollReveal>

        </div>
      </section>
    </>
  );
};

export default Calendrier;
